import { useContext } from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import EditProject from '@/types//edit-project';
import ProjectListItem from '@/types//project-list-item';
import Project, {
  ProjectUploadResponse,
  UpdateProjectVideoTranscript,
  UploadVideoToWowzaResponse,
} from '@/types//project';
import Api from './api';
import ApiV2 from './api-v2';
import ProjectShareUrls from '@/types/project-share-urls';
import { Transcript } from '@/types/transcript';
import DeleteSegment from '@/types/delete-segment';
import { UserContext } from '@/contexts/UserContext';

interface SpeakerApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/* istanbul ignore file */

export const LIST_PROJECTS_CACHE_KEY = 'list-projects';
export const MAKE_GET_PROJECTS_BY_EVENTS_CACHE_KEY = (eventIds: string[]): string =>
  `get-projects-by-events-${eventIds.sort().join(',')}`;
export const MAKE_GET_PUBLIC_PROJECTS_CACHE_KEY = (userId: string): string =>
  `get-public-projects-${userId}`;
export const MAKE_GET_PROJECT_CACHE_KEY = (id: string): string => `get-project-${id}`;
export const MAKE_GET_PROJECT_VIDEO_TRANSCRIPT_CACHE_KEY = (id: string, videoId: string): string =>
  `get-project-video-transcript-${id}-${videoId}`;
export const MAKE_GET_PROJECT_SHARE_URLS_CACHE_KEY = (id: string): string =>
  `get-project-share-urls-${id}`;
export const GET_UPLOAD_URL_CACHE_KEY = 'get-upload-url';

export function useGetProjectList(): UseQueryResult<ProjectListItem[]> {
  return useQuery(LIST_PROJECTS_CACHE_KEY, async () => {
    return await ApiV2.get<ProjectListItem[]>('/api/projects/list');
  });
}

/**
 * Fetches projects that match the provided eventIds
 * Reason: Allows managers to see projects for events they manage
 */
export function useGetProjectsByEvents(eventIds: string[]): UseQueryResult<ProjectListItem[]> {
  return useQuery(
    MAKE_GET_PROJECTS_BY_EVENTS_CACHE_KEY(eventIds),
    async () => {
      // Api.post uses the same type for request body and response, so we need to cast
      const response = await Api.post<{ eventIds: string[] }>('/projects/by-events', { eventIds });
      return response as unknown as ProjectListItem[];
    },
    {
      enabled: eventIds.length > 0,
    },
  );
}

/**
 * Fetches public projects for a user without authentication
 * Reason: Allow unauthenticated access to shared project library
 */
export function useGetPublicProjects(userId: string): UseQueryResult<{
  body: { projects: ProjectListItem[]; user: { name: string; brandKitLogo: string | null } };
  statusCode: number;
}> {
  return useQuery(
    MAKE_GET_PUBLIC_PROJECTS_CACHE_KEY(userId),
    async () => {
      return await Api.get(`/public/projects/${userId}`);
    },
    {
      enabled: !!userId,
    },
  );
}

export function useGetProject(id: string): UseQueryResult<Project> {
  // Speakers (role "user") get the segment-as-project facade from eos-app.
  // The response shape matches the editor Project DTO, so Workbench renders
  // unchanged — but the data is scoped to the speaker's single assigned
  // segment (segment MP4 as the master video, no full-stream URL).
  const { userState } = useContext(UserContext);
  const isSpeaker = userState?.role === 'user';
  return useQuery(
    [MAKE_GET_PROJECT_CACHE_KEY(id), isSpeaker ? 'speaker' : 'editor'],
    async () => {
      if (isSpeaker) {
        const response = await ApiV2.get<SpeakerApiResponse<Project>>(
          `/api/projects/${id}/as-speaker-project`,
        );
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to load project');
        }
        return response.data;
      }
      return await Api.get<Project>(`/projects/${id}`);
    },
    {
      enabled: !!id,
    },
  );
}

export function useGetProjectVideoTranscript(
  id: string,
  videoId: string,
): UseQueryResult<Transcript> {
  // Same role-branch as useGetProject. eos-app returns transcript items
  // sliced to the speaker's segment range and time-normalized to start at
  // 0, so transcript-selection clip-creation works as if the segment is
  // the entire timeline.
  const { userState } = useContext(UserContext);
  const isSpeaker = userState?.role === 'user';
  return useQuery(
    [MAKE_GET_PROJECT_VIDEO_TRANSCRIPT_CACHE_KEY(id, videoId), isSpeaker ? 'speaker' : 'editor'],
    async () => {
      if (isSpeaker) {
        const response = await ApiV2.get<SpeakerApiResponse<Transcript>>(
          `/api/projects/${id}/transcript-for-speaker`,
        );
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to load transcript');
        }
        return response.data;
      }
      return await Api.get<Transcript>(`/projects/${id}/videos/${videoId}/transcript`);
    },
    {
      enabled: !!id && !!videoId,
    },
  );
}

export function useUpdateProjectVideoTranscript(
  id: string,
  videoId: string,
): UseMutationResult<void, unknown, UpdateProjectVideoTranscript> {
  const queryClient = useQueryClient();

  return useMutation(
    async (updateProjectVideoTranscript: UpdateProjectVideoTranscript) => {
      await Api.patch(`/projects/${id}/videos/${videoId}/transcript`, updateProjectVideoTranscript);
    },
    {
      onSuccess: () =>
        queryClient.invalidateQueries([LIST_PROJECTS_CACHE_KEY, MAKE_GET_PROJECT_CACHE_KEY(id)]),
    },
  );
}

export function useGetProjectShareUrls(id: string): UseQueryResult<ProjectShareUrls> {
  return useQuery(
    MAKE_GET_PROJECT_SHARE_URLS_CACHE_KEY(id),
    async () => {
      return await Api.get(`/projects/${id}/share-urls`);
    },
    { enabled: !!id },
  );
}

export function useEditProject(id: string): UseMutationResult<void, unknown, EditProject> {
  const queryClient = useQueryClient();

  return useMutation(
    async (editProject: EditProject) => {
      await Api.patch(`/projects/${id}`, editProject);
    },
    {
      onSuccess: async () => {
        queryClient.removeQueries(MAKE_GET_PROJECT_CACHE_KEY(id), { exact: true });
        await queryClient.invalidateQueries(LIST_PROJECTS_CACHE_KEY);
      },
    },
  );
}

/**
 * Updates a project by ID (for event assignment and other updates)
 * Reason: Allows updating project properties like eventId without requiring currentProjectId state
 */
export function useUpdateProject(): UseMutationResult<
  void,
  unknown,
  { projectId: string; data: EditProject },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ projectId, data }: { projectId: string; data: EditProject }) => {
      await Api.patch(`/projects/${projectId}`, data);
    },
    {
      onSuccess: async (_, variables) => {
        queryClient.removeQueries(MAKE_GET_PROJECT_CACHE_KEY(variables.projectId), { exact: true });
        await queryClient.invalidateQueries(LIST_PROJECTS_CACHE_KEY);
      },
    },
  );
}

export function useDeleteProject(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: string) => {
      await Api.delete(`/projects/${id}`);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([LIST_PROJECTS_CACHE_KEY]),
      onError: (error: unknown, variables: string) => {
        alert(error);
      },
    },
  );
}

export function useDeleteSegment(): UseMutationResult<void, unknown, DeleteSegment, unknown> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteSegment, unknown>(
    async (deleteSegment: DeleteSegment) => {
      await Api.delete(`/projects/${deleteSegment.id}/segments/${deleteSegment.segmentId}`);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([LIST_PROJECTS_CACHE_KEY]),
      onError: (error: unknown, variables: DeleteSegment, context: unknown) => {
        alert(error);
      },
    },
  );
}

export function useGetUploadUrl(): UseMutationResult<
  ProjectUploadResponse,
  unknown,
  { fileName: string }
> {
  return useMutation(async ({ fileName }) => {
    return await Api.post<ProjectUploadResponse>(`/project/upload-url`, {
      fileName,
    } as unknown as ProjectUploadResponse);
  });
}

export function useProcessClip(): UseMutationResult<void, unknown, { id: string }> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id }) => {
      return Api.post<void>(`/project/${id}/process`, {} as unknown as void);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([LIST_PROJECTS_CACHE_KEY]),
    },
  );
}

export function useUploadVideoToWowza(): UseMutationResult<
  UploadVideoToWowzaResponse,
  unknown,
  { id: string; videoId: string; fileName: string }
> {
  return useMutation<
    UploadVideoToWowzaResponse,
    unknown,
    { id: string; videoId: string; fileName: string }
  >(async ({ id, videoId, fileName }) => {
    return await Api.post<UploadVideoToWowzaResponse>(
      `/projects/${id}/videos/${videoId}/upload-to-wowza`,
      {
        fileName,
      } as unknown as UploadVideoToWowzaResponse,
    );
  });
}

export function useUploadVideoToJwp(): UseMutationResult<
  UploadVideoToWowzaResponse,
  unknown,
  { id: string; videoId: string; fileName: string }
> {
  return useMutation<
    UploadVideoToWowzaResponse,
    unknown,
    { id: string; videoId: string; fileName: string }
  >(async ({ id, videoId, fileName }) => {
    return await Api.post<UploadVideoToWowzaResponse>(
      `/projects/${id}/videos/${videoId}/upload-to-jwp`,
      {
        fileName,
      } as unknown as UploadVideoToWowzaResponse,
    );
  });
}

export function useUploadVideoToDrive(): UseMutationResult<
  UploadVideoToWowzaResponse,
  unknown,
  { projectId: string; email: string; videoUrl: string; type: string }
> {
  return useMutation<
    UploadVideoToWowzaResponse,
    unknown,
    { projectId: string; email: string; videoUrl: string; type: string }
  >(async ({ projectId, email, videoUrl, type }) => {
    return await Api.post<UploadVideoToWowzaResponse>(`/uploadToDrive`, {
      projectId,
      email,
      videoUrl,
      type,
    } as unknown as UploadVideoToWowzaResponse);
  });
}
